/**
 * AutoAssignmentReviewDialog Component
 *
 * Review and confirm auto-assignment results
 */

import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Alert,
  Box,
  Typography,
  Paper,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningIcon from '@mui/icons-material/Warning';
import ErrorIcon from '@mui/icons-material/Error';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import IconButton from '@mui/material/IconButton';
import Collapse from '@mui/material/Collapse';
import { Fragment } from 'react';
import { AutoAssignmentResult, PatientAssignment } from '../types/intake.types';
import { useState } from 'react';

interface AutoAssignmentReviewDialogProps {
  open: boolean;
  result: AutoAssignmentResult | null;
  onConfirm: () => void;
  onCancel: () => void;
  onAssignAlternative?: (patientId: number, providerId: number, providerName: string, newDate: Date) => void;
  onAssignNextBest?: (patientId: number, providerId: number, providerName: string) => void;
  onCreateProvisional?: (patientId: number, date: Date) => void;
}

export default function AutoAssignmentReviewDialog({
  open,
  result,
  onConfirm,
  onCancel,
  onAssignAlternative,
  onAssignNextBest,
  onCreateProvisional,
}: AutoAssignmentReviewDialogProps) {
  const [expandedAlternatives, setExpandedAlternatives] = useState<Set<number>>(new Set());

  if (!result) {
    return null;
  }

  const toggleAlternatives = (patientId: number) => {
    setExpandedAlternatives((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(patientId)) {
        newSet.delete(patientId);
      } else {
        newSet.add(patientId);
      }
      return newSet;
    });
  };

  const handleAssignAlternative = (patientId: number, date: Date, providerName?: string) => {
    if (onAssignAlternative && providerName) {
      // For now, we'll use a placeholder provider ID - in real implementation, we'd need the full provider details
      const providerId = 0; // This would need to be fetched based on providerName
      onAssignAlternative(patientId, providerId, providerName, date);
    }
  };

  const handleAssignNextBest = (patientId: number, providerId: number, providerName: string) => {
    if (onAssignNextBest) {
      onAssignNextBest(patientId, providerId, providerName);
    }
  };

  const handleCreateProvisional = (patientId: number, date: Date) => {
    if (onCreateProvisional) {
      onCreateProvisional(patientId, date);
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'success':
        return <CheckCircleIcon color="success" fontSize="small" />;
      case 'conflict':
        return <WarningIcon color="warning" fontSize="small" />;
      case 'no_capacity':
        return <ErrorIcon color="error" fontSize="small" />;
      default:
        return null;
    }
  };

  const getStatusChip = (status: string) => {
    switch (status) {
      case 'success':
        return (
          <Chip
            label="Success"
            size="small"
            sx={{
              bgcolor: 'success.50',
              color: 'success.700',
              border: '1px solid',
              borderColor: 'success.200',
            }}
          />
        );
      case 'conflict':
        return (
          <Chip
            label="Potential Issue"
            size="small"
            sx={{
              bgcolor: 'warning.50',
              color: 'warning.800',
              border: '1px solid',
              borderColor: 'warning.200',
            }}
          />
        );
      case 'no_capacity':
        return (
          <Chip
            label="Not Assigned"
            size="small"
            sx={{
              bgcolor: 'error.50',
              color: 'error.700',
              border: '1px solid',
              borderColor: 'error.200',
            }}
          />
        );
      default:
        return (
          <Chip
            label={status}
            size="small"
            sx={{
              bgcolor: 'grey.100',
              color: 'grey.700',
              border: '1px solid',
              borderColor: 'grey.300',
            }}
          />
        );
    }
  };

  const renderAlternatives = (assignment: PatientAssignment) => {
    if (!assignment.alternatives) return null;

    const { alternativeDates, nextBestProviders, provisionalOption } = assignment.alternatives;

    return (
      <Box>
        <Typography variant="subtitle2" fontWeight={600} gutterBottom sx={{ color: 'error.main' }}>
          Recourse Options
        </Typography>

        {/* Alternative Dates */}
        {alternativeDates.length > 0 && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="caption" fontWeight={600} display="block" gutterBottom>
              Alternative Dates
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {alternativeDates.map((alt, idx) => (
                <Box
                  key={idx}
                  sx={{
                    p: 1.5,
                    bgcolor: 'white',
                    borderRadius: 1,
                    border: '1px solid',
                    borderColor: 'divider',
                  }}
                >
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box>
                      <Typography variant="body2" fontWeight={600}>
                        {alt.date.toLocaleDateString()} ({alt.daysOffset > 0 ? '+' : ''}{alt.daysOffset} days)
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {alt.availableProvidersCount} provider{alt.availableProvidersCount !== 1 ? 's' : ''} available
                        {alt.bestProviderName && ` • Best: ${alt.bestProviderName}`}
                      </Typography>
                    </Box>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => handleAssignAlternative(assignment.patientId, alt.date, alt.bestProviderName)}
                    >
                      Assign
                    </Button>
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>
        )}

        {/* Next Best Providers */}
        {nextBestProviders.length > 0 && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="caption" fontWeight={600} display="block" gutterBottom>
              Next Best Providers (with warnings)
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {nextBestProviders.map((provider, idx) => (
                <Box
                  key={idx}
                  sx={{
                    p: 1.5,
                    bgcolor: 'white',
                    borderRadius: 1,
                    border: '1px solid',
                    borderColor: 'warning.main',
                  }}
                >
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box>
                      <Typography variant="body2" fontWeight={600}>
                        {provider.providerName} ({provider.territory})
                      </Typography>
                      <Typography variant="caption" color="warning.dark" display="block">
                        ⚠ {provider.warning}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {provider.utilizationPercent}% utilized • {provider.availableMinutes} min available
                      </Typography>
                    </Box>
                    <Button
                      size="small"
                      variant="outlined"
                      color="warning"
                      onClick={() => handleAssignNextBest(assignment.patientId, provider.providerId, provider.providerName)}
                    >
                      Assign
                    </Button>
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>
        )}

        {/* Provisional/Marketplace Option */}
        {provisionalOption && (
          <Box>
            <Typography variant="caption" fontWeight={600} display="block" gutterBottom>
              Marketplace Option
            </Typography>
            <Box
              sx={{
                p: 1.5,
                bgcolor: 'white',
                borderRadius: 1,
                border: '1px solid',
                borderColor: 'info.main',
              }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                  <Typography variant="body2" fontWeight={600}>
                    {provisionalOption.suggestedAction}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {provisionalOption.description}
                  </Typography>
                </Box>
                <Button
                  size="small"
                  variant="outlined"
                  color="info"
                  onClick={() => handleCreateProvisional(assignment.patientId, provisionalOption.intakeVisitDate)}
                >
                  Create
                </Button>
              </Box>
            </Box>
          </Box>
        )}
      </Box>
    );
  };

  return (
    <Dialog open={open} onClose={onCancel} maxWidth="lg" fullWidth>
      <DialogTitle>Review Auto-Assignment Results</DialogTitle>
      <DialogContent>
        {/* Summary */}
        <Alert
          severity={result.unassignedCount > 0 ? 'warning' : result.conflictCount > 0 ? 'info' : 'success'}
          sx={{ mb: 3 }}
        >
          <Typography variant="body2">
            <strong>{result.summary}</strong>
          </Typography>
        </Alert>

        {/* Statistics */}
        <Box sx={{ mb: 3, display: 'flex', gap: 2 }}>
          <Paper sx={{ p: 2, flex: 1, textAlign: 'center' }}>
            <Typography variant="h4" color="success.main">
              {result.successCount}
            </Typography>
            <Typography variant="caption">Successful</Typography>
          </Paper>
          <Paper sx={{ p: 2, flex: 1, textAlign: 'center' }}>
            <Typography variant="h4" color="warning.main">
              {result.conflictCount}
            </Typography>
            <Typography variant="caption">With Issues</Typography>
          </Paper>
          <Paper sx={{ p: 2, flex: 1, textAlign: 'center' }}>
            <Typography variant="h4" color="error.main">
              {result.unassignedCount}
            </Typography>
            <Typography variant="caption">Unassigned</Typography>
          </Paper>
        </Box>

        {/* Assignment Details */}
        <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell width={50} />
                <TableCell>Patient</TableCell>
                <TableCell>Intake Date</TableCell>
                <TableCell>Assigned Provider</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Issues</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {result.assignments.map((assignment) => {
                const hasAlternatives = assignment.status === 'no_capacity' && assignment.alternatives;
                const isExpanded = expandedAlternatives.has(assignment.patientId);

                return (
                  <Fragment key={assignment.patientId}>
                    <TableRow
                      hover
                      onClick={hasAlternatives ? () => toggleAlternatives(assignment.patientId) : undefined}
                      sx={{
                        bgcolor: assignment.status === 'no_capacity' ? '#ffebee' : 'inherit',
                        cursor: hasAlternatives ? 'pointer' : 'default',
                        '&:hover': {
                          bgcolor: assignment.status === 'no_capacity' ? '#ffcdd2' : undefined,
                        },
                      }}
                    >
                      <TableCell>
                        {hasAlternatives ? (
                          <IconButton size="small">
                            {isExpanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                          </IconButton>
                        ) : (
                          getStatusIcon(assignment.status)
                        )}
                      </TableCell>
                      <TableCell>
                        <strong>{assignment.patientName}</strong>
                      </TableCell>
                      <TableCell>{assignment.intakeVisitDate.toLocaleDateString()}</TableCell>
                      <TableCell>
                        {assignment.providerName !== 'None' ? (
                          assignment.providerName
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            Not assigned
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>{getStatusChip(assignment.status)}</TableCell>
                      <TableCell>
                        {assignment.issues && assignment.issues.length > 0 ? (
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                            {assignment.issues.map((issue, idx) => (
                              <Typography key={idx} variant="caption" color="text.secondary">
                                • {issue}
                              </Typography>
                            ))}
                          </Box>
                        ) : (
                          <Typography variant="caption" color="text.secondary">
                            None
                          </Typography>
                        )}
                      </TableCell>
                    </TableRow>
                    {hasAlternatives && (
                      <TableRow>
                        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={6}>
                          <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                            <Box sx={{ py: 2, px: 2, bgcolor: '#fafafa' }}>
                              {renderAlternatives(assignment)}
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    )}
                  </Fragment>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel}>Cancel</Button>
        <Button onClick={onConfirm} variant="contained" color="primary">
          Confirm Assignments
        </Button>
      </DialogActions>
    </Dialog>
  );
}
